import { Box, Text, useMediaQuery } from '@chakra-ui/react'
import type { EventInput } from '@fullcalendar/core'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import PlusIcon from '../../../icons/calendar/plus-icon'

interface CalendarHoverPillProps {
  calendarDOMRef: React.RefObject<HTMLDivElement>
  scheduleEvents: EventInput[]
}

type HoveredSlot = {
  date: Date
  top: number
  left: number
  width: number
  height: number
} | null

function isOverlap(
  startA: moment.Moment,
  endA: moment.Moment,
  startB: moment.Moment,
  endB: moment.Moment
) {
  return startA.isBefore(endB) && endA.isAfter(startB)
}

// Custom hook for desktop detection
function useIsDesktop(): boolean {
  const [isDesktop] = useMediaQuery('(min-width: 62em)') // 992px+
  return isDesktop
}

export const CalendarHoverPill: React.FC<CalendarHoverPillProps> = ({
  calendarDOMRef,
  scheduleEvents
}) => {
  const [hoveredSlot, setHoveredSlot] = useState<HoveredSlot>(null)
  const [lastMouseEvent, setLastMouseEvent] = useState<MouseEvent | null>(null)
  const isDesktop = useIsDesktop()

  useEffect(() => {
    const calendarEl = calendarDOMRef.current
    if (!calendarEl) return

    const slots = Array.from(
      calendarEl.querySelectorAll<HTMLTableCellElement>(
        '.fc-timegrid-slot-lane[data-time]'
      )
    )
    const cols = Array.from(
      calendarEl.querySelectorAll<HTMLTableCellElement>(
        '.fc-timegrid-col[data-date]'
      )
    )

    const handleMouseMove = (e: MouseEvent) => {
      setLastMouseEvent(e)
      //not in grid
      const headerRect = calendarEl
        .querySelector('.fc-scroller-harness')
        ?.getBoundingClientRect()
      if (
        headerRect &&
        e.clientY >= headerRect.top &&
        e.clientY <= headerRect.bottom
      )
        return setHoveredSlot(null)
      const gridRect = calendarEl
        .querySelector('.fc-scroller.fc-scroller-liquid-absolute')
        ?.getBoundingClientRect()
      if (gridRect && (e.clientY < gridRect.top || e.clientY > gridRect.bottom))
        return setHoveredSlot(null)
      if (
        (
          document.elementFromPoint(e.clientX, e.clientY) as HTMLElement
        )?.closest('.fc-event')
      )
        return setHoveredSlot(null)

      const hoveredSlotEl = slots.find(slot => {
        const rect = slot.getBoundingClientRect()
        return (
          e.clientY >= rect.top &&
          e.clientY <= rect.bottom &&
          e.clientX >= rect.left &&
          e.clientX <= rect.right
        )
      })
      if (!hoveredSlotEl) return setHoveredSlot(null)
      const timeStr = hoveredSlotEl.getAttribute('data-time')
      if (!timeStr) return setHoveredSlot(null)

      const hoveredCol = cols.find(col => {
        const colRect = col.getBoundingClientRect()
        return (
          e.clientX >= colRect.left &&
          e.clientX <= colRect.right &&
          e.clientY >= colRect.top &&
          e.clientY <= colRect.bottom
        )
      })
      if (!hoveredCol) return setHoveredSlot(null)
      const dateStr = hoveredCol.getAttribute('data-date')
      if (!dateStr) return setHoveredSlot(null)

      //snap to 30-min slot
      const dateTime = moment(`${dateStr}T${timeStr}`)
      const snappedMinute = dateTime.minutes() < 30 ? 0 : 30
      dateTime.minutes(snappedMinute).seconds(0)
      const slotStart = dateTime.clone()
      const slotEnd = dateTime.clone().add(30, 'minutes')

      //overlap in 30-min segment
      const isTaken = (start: moment.Moment, end: moment.Moment) =>
        scheduleEvents.some(event => {
          if (!event.start || !event.end) return false
          if (event.allDay) return false
          const eventStart = moment(event.start)
          const eventEnd = moment(event.end)
          return isOverlap(start, end, eventStart, eventEnd)
        })
      if (isTaken(slotStart, slotEnd)) return setHoveredSlot(null)

      const targetSlot = slots.find(
        s => s.getAttribute('data-time') === dateTime.format('HH:mm:ss')
      )
      const nextSlot = slots.find(
        s =>
          s.getAttribute('data-time') ===
          dateTime.clone().add(30, 'minutes').format('HH:mm:ss')
      )
      if (!targetSlot || !nextSlot) return setHoveredSlot(null)
      const slotRect = targetSlot.getBoundingClientRect()
      const nextRect = nextSlot.getBoundingClientRect()
      const colRect = hoveredCol.getBoundingClientRect()
      const parentRect = calendarEl.getBoundingClientRect()
      setHoveredSlot({
        date: dateTime.toDate(),
        top: slotRect.top - parentRect.top,
        left: colRect.left - parentRect.left,
        width: colRect.width,
        height: nextRect.top - slotRect.top
      })
    }

    calendarEl.addEventListener('mousemove', handleMouseMove)
    const scrollContainer = calendarEl.querySelector(
      '.fc-scroller.fc-scroller-liquid-absolute'
    )
    const handleScroll = () => {
      if (lastMouseEvent) handleMouseMove(lastMouseEvent)
    }
    scrollContainer?.addEventListener('scroll', handleScroll)

    return () => {
      calendarEl.removeEventListener('mousemove', handleMouseMove)
      scrollContainer?.removeEventListener('scroll', handleScroll)
    }
  }, [calendarDOMRef, scheduleEvents, lastMouseEvent])

  //render on desktop
  if (!isDesktop) return null

  //only render if fully visible
  if (!hoveredSlot || !calendarDOMRef.current) return null
  const scrollBody = calendarDOMRef.current.querySelector(
    '.fc-scrollgrid-section.fc-scrollgrid-section-body.fc-scrollgrid-section-liquid'
  ) as HTMLElement | null
  let shouldRenderPill = false
  if (scrollBody) {
    const bodyRect = scrollBody.getBoundingClientRect()
    const parentRect = calendarDOMRef.current.getBoundingClientRect()
    const pillTop = parentRect.top + hoveredSlot.top + 2
    const pillBottom = pillTop + hoveredSlot.height + 2
    if (pillTop >= bodyRect.top && pillBottom <= bodyRect.bottom)
      shouldRenderPill = true
  }
  if (!shouldRenderPill) return null

  let pillElement = null
  if (shouldRenderPill) {
    pillElement = (
      <Box
        key={`${hoveredSlot.top}-${hoveredSlot.left}`}
        position="absolute"
        top={`${hoveredSlot.top}px`}
        left={`${hoveredSlot.left}px`}
        width={`${hoveredSlot.width}px`}
        height={`${hoveredSlot.height}px`}
        bg="#F7F9FE"
        border="1px solid"
        borderColor="rgba(40, 40, 40, 0.05)"
        borderRadius="999px"
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        flexShrink={0}
        pointerEvents="none"
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        pt="4px"
        pb="4px"
        pl="8px"
        pr="6px"
        zIndex={99}
        style={
          {
            '--pill-margin': '6px',
            '--pill-radius': '32px',
            '--pill-opacity': '0',
            animation: 'pillFadeIn 150ms ease-in-out forwards'
          } as any
        }
        sx={{
          '@keyframes pillFadeIn': {
            '0%': {
              margin: 'var(--pill-margin)',
              borderRadius: 'var(--pill-radius)',
              opacity: 'var(--pill-opacity)'
            },
            '100%': {
              margin: '0px',
              borderRadius: '999px',
              opacity: 1
            }
          }
        }}
      >
        <Text
          isTruncated
          flex="1"
          overflow="hidden"
          color="#2B42B3"
          textOverflow="ellipsis"
          fontFamily="TofinoPersonal"
          fontSize="10px"
          fontStyle="normal"
          fontWeight="700"
          lineHeight="145%"
          letterSpacing="0px"
        >
          New appointment
        </Text>
        <Box
          ml={2}
          width="16px"
          height="16px"
          flexShrink={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <PlusIcon width={16} height={16} />
        </Box>
      </Box>
    )
  }

  return pillElement
}
